/* eslint-disable sonarjs/no-duplicate-string */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Alert, Card, Typography, message } from 'antd';
import { DownloadOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getSignedUrl } from 'store/commonSlice';
import { ACL } from 'components/common';

const { Text } = Typography;
const LABELS = {
	APPROVED_BY: 'Approved By',
	REJECTED_BY: 'Rejected By',
	UPLOADED_BY: 'Uploaded By',
	STATUS: 'Status',
};

const STYLES = {
	DASHED_BORDER: '2px dashed #1890ff',
};

const normalizeToArray = (value) => {
	if (Array.isArray(value)) return value;
	if (value) return [value];
	return [];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function PODTab({ podData, podLoading, podError, podKey, podUploadData, isV2 = false }) {
	const dispatch = useDispatch();

	const podItems = useMemo(() => {
		if (!podData) return [];
		const proofUrls = normalizeToArray(podData.proof_of_delivery_url);
		const driverPodUrls = normalizeToArray(podData.driver_pod_url);

		return [
			...proofUrls.map((url) => ({
				url,
				uploadedBy: podUploadData?.created_by || 'N/A',
			})),
			...driverPodUrls.map((url) => ({
				url,
				uploadedBy: podUploadData?.driver_name || 'N/A',
			})),
		];
	}, [podData, podUploadData]);

	const podUrls = useMemo(() => podItems.map((item) => item.url), [podItems]);

	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (activeIndex >= podUrls.length && podUrls.length > 0) {
			setActiveIndex(0);
		}
	}, [activeIndex, podUrls.length]);

	const goPrev = () => setActiveIndex((i) => (i > 0 ? i - 1 : podUrls.length - 1));
	const goNext = () => setActiveIndex((i) => (i < podUrls.length - 1 ? i + 1 : 0));

	const getFilename = (url) => {
		if (!url) return 'Document';
		if (url.startsWith('blob:')) return 'Document';
		try {
			const filename = url.split('/').pop().split('?')[0];
			return decodeURIComponent(filename);
		} catch {
			return 'Document';
		}
	};

	const getStatusColor = (status) => {
		if (status === 'Accepted') return '#52c41a';
		if (status === 'Rejected') return '#ff4d4f';
		return '#faad14';
	};

	const handlePODDownload = async () => {
		if (!podKey) {
			message.error('POD key not available');
			return;
		}

		try {
			let keysArray = [podKey];
			if (isV2 && typeof podKey === 'string') {
				keysArray = podKey
					.split(',')
					.map((k) => k.trim())
					.filter(Boolean);
			}
			const data = {
				url_type: 'download',
				data: {
					entity: 'proof_of_delivery_upload',
					keys: keysArray,
				},
			};

			const response = await dispatch(getSignedUrl(data));
			if (isV2 && response?.data && response.data.length > 0) {
				response.data.forEach((item) => {
					if (item?.url) window.open(item.url, '_blank');
				});
			} else if (response?.data && response.data[0]?.url) {
				window.open(response.data[0].url, '_blank');
			} else {
				message.error('Failed to get download URL');
			}
		} catch (error) {
			message.error('Failed to download POD');
		}
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: 24,
					padding: '0 0 24px 0',
					background: 'transparent',
					borderRadius: 8,
					minHeight: 56,
					borderBottom: '1px solid #eaeaea',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					{podData && (
						<ACL action="search" module="proof_of_delivery">
							<Button
								type="default"
								icon={<DownloadOutlined />}
								onClick={handlePODDownload}
								disabled={!podKey}
								style={{
									minWidth: 160,
									fontWeight: 500,
									border: '2px solid #1890ff',
									color: '#1890ff',
									background: '#fff',
								}}
							>
								DOWNLOAD POD
							</Button>
						</ACL>
					)}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						flex: 1,
						justifyContent: 'center',
						gap: 48,
					}}
				>
					{podUploadData && (
						<>
							<div style={{ textAlign: 'center', minWidth: 120 }}>
								<div
									style={{
										fontSize: 14,
										color: '#888',
										marginBottom: 2,
									}}
								>
									{LABELS.UPLOADED_BY}
								</div>
								<div
									style={{
										fontWeight: 400,
										fontSize: 16,
										color: '#222',
									}}
								>
									{podUploadData.created_by || 'N/A'}
								</div>
							</div>
							<div
								style={{
									height: 32,
									borderLeft: STYLES.DASHED_BORDER,
									margin: '0 24px',
								}}
							/>
							{podUploadData.status === 'Accepted' && podUploadData.updated_by && (
								<>
									<div style={{ textAlign: 'center', minWidth: 120 }}>
										<div
											style={{
												fontSize: 14,
												color: '#888',
												marginBottom: 2,
											}}
										>
											{LABELS.APPROVED_BY}
										</div>
										<div
											style={{
												fontWeight: 400,
												fontSize: 16,
												color: '#222',
											}}
										>
											{podUploadData.updated_by}
										</div>
									</div>
									``
									<div
										style={{
											height: 32,
											borderLeft: STYLES.DASHED_BORDER,
											margin: '0 24px',
										}}
									/>
								</>
							)}
							{podUploadData.status === 'Rejected' && podUploadData.rejected_by && (
								<>
									<div style={{ textAlign: 'center', minWidth: 120 }}>
										<div
											style={{
												fontSize: 14,
												color: '#888',
												marginBottom: 2,
											}}
										>
											{LABELS.REJECTED_BY}
										</div>
										<div
											style={{
												fontWeight: 400,
												fontSize: 16,
												color: '#222',
											}}
										>
											{podUploadData.rejected_by}
										</div>
									</div>
									<div
										style={{
											height: 32,
											borderLeft: STYLES.DASHED_BORDER,
											margin: '0 24px',
										}}
									/>
								</>
							)}
							<div style={{ textAlign: 'center', minWidth: 120 }}>
								<div
									style={{
										fontSize: 14,
										color: '#888',
										marginBottom: 2,
									}}
								>
									{LABELS.STATUS}
								</div>
								<div
									style={{
										fontWeight: 400,
										fontSize: 16,
										color: '#222',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 8,
									}}
								>
									<span
										style={{
											color: getStatusColor(podUploadData.status),
										}}
									>
										{podUploadData.status || 'Pending'}
									</span>
									{podUploadData.status && (
										<span
											style={{
												display: 'inline-block',
												width: 8,
												height: 8,
												borderRadius: '50%',
												backgroundColor: getStatusColor(
													podUploadData.status
												),
											}}
										/>
									)}
								</div>
							</div>
						</>
					)}
				</div>
				<div style={{ minWidth: 120 }}></div>
			</div>
			<Card style={{ marginTop: 16, borderRadius: 8 }} bodyStyle={{ padding: 24 }}>
				{podLoading && <Spin tip="Loading POD data..." />}
				{podError && <Alert type="error" message={podError} />}
				{!podLoading && !podError && isV2 && podUrls.length > 0 && (
					<div style={{ display: 'flex', gap: '24px' }}>
						<div
							style={{
								width: '240px',
								background: '#f8f9fa',
								borderRadius: '8px',
								padding: '16px 0',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{podUrls.map((url, idx) => {
								const isActive = activeIndex === idx;
								const filename = getFilename(url);
								return (
									// eslint-disable-next-line
									<div
										key={idx} // eslint-disable-line react/no-array-index-key
										role="button"
										tabIndex={0}
										onClick={() => setActiveIndex(idx)}
										style={{
											padding: '12px 24px',
											cursor: 'pointer',
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											background: isActive ? '#fff' : 'transparent',
											boxShadow: isActive
												? '0 1px 4px rgba(0,0,0,0.05)'
												: 'none',
											borderLeft: isActive
												? '4px solid #1f3e5a'
												: '4px solid transparent',
											marginBottom: '4px',
										}}
									>
										<Text
											style={{
												color: isActive ? '#222' : '#8c8c8c',
												fontWeight: isActive ? 600 : 400,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												maxWidth: '160px',
											}}
											title={filename}
										>
											{filename}
										</Text>
										{isActive && (
											<RightOutlined
												style={{ color: '#222', fontSize: '12px' }}
											/>
										)}
									</div>
								);
							})}
						</div>
						<div
							style={{
								flex: 1,
								background: '#fff',
								border: '1px solid #f0f0f0',
								borderRadius: '8px',
								overflow: 'hidden',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<div
								style={{
									padding: '16px 24px',
									borderBottom: '1px solid #f0f0f0',
									display: 'flex',
									justifyContent: 'right',
									background: '#fafafa',
								}}
							>
								<Text style={{ color: '#595959', fontSize: 14 }}>
									Uploaded by :{' '}
									<span style={{ fontWeight: 600, color: '#1f3e5a' }}>
										{podItems[activeIndex]?.uploadedBy ||
											podUploadData?.created_by ||
											'N/A'}
									</span>
								</Text>
							</div>
							<div
								style={{
									position: 'relative',
									flex: 1,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '24px',
									minHeight: '500px',
									background: '#fff',
								}}
							>
								{podUrls.length > 1 && (
									<Button
										type="text"
										icon={
											<LeftOutlined
												style={{ fontSize: '24px', color: '#8c8c8c' }}
											/>
										}
										onClick={goPrev}
										style={{
											position: 'absolute',
											left: '16px',
											zIndex: 10,
											height: '60px',
											width: '40px',
										}}
									/>
								)}

								<div
									style={{
										width: '100%',
										maxWidth: '80%',
										maxHeight: '600px',
										textAlign: 'center',
									}}
								>
									{podUrls[activeIndex].toLowerCase().includes('.pdf') ? (
										<iframe
											title={`POD PDF ${activeIndex}`}
											src={podUrls[activeIndex]}
											width="100%"
											height="600"
											style={{
												border: '1px solid #d9d9d9',
												borderRadius: '6px',
												minWidth: '600px',
											}}
										/>
									) : (
										<img
											src={podUrls[activeIndex]}
											alt={`Proof of Delivery ${activeIndex + 1}`}
											style={{
												maxWidth: '100%',
												maxHeight: '500px',
												border: '1px solid #d9d9d9',
												borderRadius: '6px',
												objectFit: 'contain',
											}}
										/>
									)}
								</div>

								{podUrls.length > 1 && (
									<Button
										type="text"
										icon={
											<RightOutlined
												style={{ fontSize: '24px', color: '#8c8c8c' }}
											/>
										}
										onClick={goNext}
										style={{
											position: 'absolute',
											right: '16px',
											zIndex: 10,
											height: '60px',
											width: '40px',
										}}
									/>
								)}
							</div>
							{podUrls.length > 1 && (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										gap: '8px',
										padding: '16px',
										background: '#fafafa',
										borderTop: '1px solid #f0f0f0',
									}}
								>
									{podUrls.map((_, idx) => (
										// eslint-disable-next-line
										<div
											key={idx} // eslint-disable-line react/no-array-index-key
											role="button"
											tabIndex={0}
											onClick={() => setActiveIndex(idx)}
											style={{
												width: '8px',
												height: '8px',
												borderRadius: '50%',
												background:
													activeIndex === idx ? '#1f3e5a' : '#d9d9d9',
												cursor: 'pointer',
												transition: 'background 0.3s ease',
											}}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				)}
				{!podLoading && !podError && !isV2 && podData && (
					<div>
						{podUrls[activeIndex] && (
							<div style={{ textAlign: 'center', marginTop: 24 }}>
								{podUrls[activeIndex].toLowerCase().includes('.pdf') ? (
									<iframe
										title="POD PDF"
										src={podUrls[activeIndex]}
										width="100%"
										height="600"
										style={{
											border: '1px solid #d9d9d9',
											borderRadius: '6px',
										}}
									/>
								) : (
									<img
										src={podUrls[activeIndex]}
										alt="Proof of Delivery"
										style={{
											maxWidth: '100%',
											maxHeight: '600px',
											border: '1px solid #d9d9d9',
											borderRadius: '6px',
											objectFit: 'contain',
										}}
									/>
								)}
							</div>
						)}
					</div>
				)}
				{!podLoading &&
					!podError &&
					((isV2 && podUrls.length === 0) || (!isV2 && !podData)) && (
						<div style={{ padding: '40px 0', textAlign: 'center' }}>
							<Text type="secondary" style={{ fontSize: 16 }}>
								No POD data found for this trip.
							</Text>
						</div>
					)}
			</Card>
		</>
	);
}

PODTab.propTypes = {
	podData: PropTypes.shape({
		proof_of_delivery_url: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string),
		]),
		driver_pod_url: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string),
		]),
		url: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
		uploaded_by: PropTypes.string,
		node_name: PropTypes.string,
		status: PropTypes.string,
		approved_by: PropTypes.string,
		rejected_by: PropTypes.string,
	}),
	podLoading: PropTypes.bool,
	podError: PropTypes.string,
	podKey: PropTypes.string,
	isV2: PropTypes.bool,
	podUploadData: PropTypes.shape({
		uploaded_by: PropTypes.string,
		node_name: PropTypes.string,
		status: PropTypes.string,
		approved_by: PropTypes.string,
		rejected_by: PropTypes.string,
		created_by: PropTypes.string,
		updated_by: PropTypes.string,
		driver_name: PropTypes.string,
	}),
};

export default PODTab;
